import { Injectable, inject, isDevMode } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, retry } from 'rxjs/operators';
import { StrapiService } from './strapi.service';
import { Global, StrapiEntry, CmsLoadResult } from '../models/strapi.types';

@Injectable({ providedIn: 'root' })
export class GlobalService {
  private readonly strapi = inject(StrapiService);

  // Cacheado: solo se fetcha una vez por sesión
  private _global$: Observable<CmsLoadResult<StrapiEntry<Global>>> | null = null;

  get(): Observable<CmsLoadResult<StrapiEntry<Global>>> {
    if (!this._global$) {
      this._global$ = this.strapi
        .getSingle<Global>('global', {
          // Query LIVIANA y confiable (200). La query pesada de 14 params devolvía
          // 500 en Strapi (combinación de populates redundantes + media legacy),
          // lo que forzaba el fallback y ocultaba las ediciones del CMS.
          // Solo se pide lo que consumen Navbar, Footer y Home.
          // Header (navItems + scalars ctaLabel/ctaUrl/ctaIsVisible/isVisible)
          'populate[globalHeader][populate][navItems]': 'true',
          'populate[globalHeader][populate][logoHeader]': 'true',
          'populate[globalHeader][populate][logoFoton]': 'true',
          'populate[globalHeader][populate][logoAndesMotor]': 'true',
          // Footer (logo, columnas+links, legales; brandText/copyright/isVisible son scalars)
          'populate[globalFooter][populate][logoFooter]': 'true',
          'populate[globalFooter][populate][footerColumns][populate][links]': 'true',
          'populate[globalFooter][populate][legalLinks]': 'true',
          // Contacto (scalars) y Redes (imagen única que renderiza el footer)
          'populate[globalContacto]': 'true',
          'populate[globalRedes][populate][socialImage]': 'true',
          // Repeatables que consume Home
          'populate[statsBar]': 'true',
          'populate[homeNosotrosStats]': 'true',
          // SEO por defecto (SeoDefaultsService). Probado contra APIM: la query sigue en 200.
          'populate[seoDefaultImage]': 'true',
        })
        .pipe(
          // Reintenta ante 500/502 intermitentes del backend antes de caer al fallback.
          retry({ count: 2, delay: 1000 }),
          map(data => ({ data, source: 'cms' as const })),
          catchError(err => {
            if (isDevMode()) {
              console.error('[CMS ERROR] GlobalService:', err);
            }
            // No cachear el fallback de forma permanente: si el backend estaba caído,
            // la próxima suscripción vuelve a intentar contra el CMS en vez de quedar
            // atrapada en el fallback toda la sesión.
            this._global$ = null;
            return of({
              data: null,
              source: 'error' as const,
              error: err,
            });
          }),
          shareReplay(1)
        );
    }
    return this._global$;
  }

  /**
   * Obtiene un atributo específico del Global con fallback a valor por defecto.
   * Útil para componentes que necesitan un campo específico sin cargar toda la configuración.
   */
  getAttrSafe<K extends keyof Global>(key: K): Observable<Global[K] | null> {
    return this.get().pipe(
      map((result) => (result.data?.attributes?.[key] ?? null) as Global[K] | null)
    );
  }

  /** Acceso directo a atributos con fallback */
  getAttr<K extends keyof Global>(key: K, fallback: Global[K]): Observable<Global[K]> {
    return this.get().pipe(
      map((result) => (result.data?.attributes?.[key] ?? fallback) as Global[K])
    );
  }
}
